import { defineStore } from 'pinia';
import { UpgradeMotorService as _UpgradeMotor } from '@/services/store/upgrademotor.service';
import { computed, ref } from 'vue';
import {
  UpgradeCoverageUpdateModel,
  ApplyRulesResponseOfUpgradeCoverageViewModel,
  InsurancePolicy,
  QuoteTypeCodes,
  InsuranceQuoteType,
  VehicleCoverage,
  UpgradeCoverageViewModel,
  getInsuranceTypeDisplayName,
} from '@/interfaces';
import { QuotePremiumDetails } from '@/interfaces/entities/premium-details.interface';
import {
  IJeopardy,
  INameCodeWithDefault,
  IReviewSummary,
  IServiceResponse,
  IServiceResponseError,
  ISitecoreReviewSummaryItem,
} from '@/interfaces/entities';
import { JP_GENERIC_CODE } from '@/constants/jeopardy-codes.const';
import {
  ProcessPaymentRequest,
  QuoteSession,
  QuoteSessionService as _quoteSessionService,
} from '@/services/store/quote-session.service';
import { FormEvents } from '@/enums/form-events.enum';
import { IAnalyticsPaymentDetail, IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { useAnalyticsStore } from '../analytics/analytics.state';
import { toBool, toCurrency } from '@/utils/convert';
import { CarService } from '@/services/store/car.service';
import { MotorOptionalBenefits, MotorOptionalBenefitsSitecoreItem } from '@/enums/motor-optional-benefits.enum';
import { format } from 'date-fns';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { useReferenceDataStore } from '../reference-data/reference-data.state';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { useReferenceDataIQMotorStore } from '../reference-data/reference-data-iq-motor.state';
import { QuoteVehicleValuationMethods, VehicleValueType } from '@/enums/quote-vehicle-valudation-methods.enum';

export const useUpgradeMotorStore = defineStore('UpgradeMotor', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const quoteSessionId = ref<string>();
  const currentPolicy = ref<InsurancePolicy>();
  const model = ref<UpgradeCoverageUpdateModel>();
  const quotePremiumDetails = ref<QuotePremiumDetails>();
  const jeopardies = ref<IJeopardy[]>([]);
  const errors = ref<IServiceResponseError[]>([]);
  const businessRuleResponse = ref<ApplyRulesResponseOfUpgradeCoverageViewModel>();
  const quoteSession = ref<IServiceResponse<QuoteSession>>();
  const excessData = ref<INameCodeWithDefault[]>();
  const paymentReceiptNumber = ref<string>();
  const analyticsStore = useAnalyticsStore();
  const refStoreCommon = useReferenceDataStore();
  const refMotorStore = useReferenceDataIQMotorStore();

  const currentCoverage = computed(
    () =>
      currentPolicy.value?.coverages.filter(
        (x) => x.id == quoteSession.value?.data?.coverageIds[0]
      )[0] as VehicleCoverage
  );
  const effectiveDateCutoff = new Date('2024-12-10'); // This will most likely not change and can be removed after the 10th of December 2025
  const riskEffectiveDateBefore10Dec2024 = computed(
    () => currentCoverage.value?.riskEffectiveDate && currentCoverage.value.riskEffectiveDate < effectiveDateCutoff
  );

  // ------------------------------------------------------------- //
  // Define Functions
  // ------------------------------------------------------------- //

  /**
   * Get Policy
   */
  async function getPolicy() {
    const params = new URLSearchParams(location.search);
    quoteSessionId.value = params.get('quoteSessionId') ?? '';
    if (!quoteSessionId.value) throw JP_GENERIC_CODE;
    const response = await _UpgradeMotor.getPolicy(quoteSessionId.value);
    errors.value = response.errors;
    if (response.success && response.data) {
      currentPolicy.value = response.data;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
  }

  /**
   * Apply Business Rules
   */
  async function applyBusinessRules(requestQuote?: boolean, bindChange?: boolean) {
    const response = await _UpgradeMotor.applyBusinessRules({
      quoteSessionId: quoteSessionId.value,
      model: model.value,
      requestQuote: requestQuote,
      bindChange: bindChange,
    });
    errors.value = response.errors;
    if (response.success && response.data) {
      jeopardies.value = response.data.jeopardies;
      quotePremiumDetails.value = response.data?.viewModel?.quotePremiumDetails;
      businessRuleResponse.value = response.data;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
    if (jeopardies.value && jeopardies.value.length > 0) {
      throw jeopardies.value[0].code;
    }
  }

  const getQuoteSession = async () => {
    const response = await _quoteSessionService.getQuoteSession(quoteSessionId.value ?? '');
    errors.value = response.errors;
    if (response.success && response.data) {
      quoteSession.value = response;
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE;
    }
    if (jeopardies.value && jeopardies.value.length > 0) {
      throw jeopardies.value[0].code;
    }
  };

  /**
   * Push data to analytics
   *
   * @param options
   */
  const pushAnalytics = (
    options: {
      event: FormEvents;
      amount?: number;
      payment?: Partial<IAnalyticsPaymentDetail>;
      includeReviewFields?: boolean;
    },
    dictionary: Record<string, string>
  ) => {
    try {
      const vehicle = currentCoverage.value?.vehicle;

      const analyticsModel: IFormAnalytics = {
        event: options.event,
        form: {
          productName: getInsuranceTypeDisplayName(dictionary, currentCoverage.value?.coverageCode ?? ''),
          detail: {
            makeName: vehicle?.make,
            modelName: vehicle?.model,
            vehicleYear: vehicle?.year,
          },
        },
      };

      if (options.includeReviewFields) {
        analyticsModel.form.detail!.agreedValue = model.value?.agreedVehicleValue?.toString();
        analyticsModel.form.detail!.valueCategory = model.value?.agreedVehicleValue ? 'Agreed value' : 'Market value';
        analyticsModel.form.detail!.excess = Number(model.value?.standardExcess ?? 0);
        const optionalBenefits = [] as string[];
        if (model.value?.includeCarHire) {
          optionalBenefits.push('Hire car');
        }
        if (model.value?.includeNcdp) {
          optionalBenefits.push('NCD Protection');
        }
        if (model.value?.includeWindscreenReplacement) {
          optionalBenefits.push('Windscreen');
        }
        analyticsModel.form.detail!.optionalBenefits = optionalBenefits.join(', ');
      }

      if (options.amount) {
        analyticsModel.form.amount = options.amount;
      }
      if (options.payment) {
        analyticsModel.payment = options.payment;
      }
      analyticsStore.pushAnalytics(analyticsModel);
    } catch (e) {
      console.error('Error pushing to analytics', e);
    }
  };

  const getMotorExcess = async (quoteType: string, startDate?: string): Promise<INameCodeWithDefault[]> => {
    const excess = await CarService.getMotorExcess(quoteType, startDate);
    excessData.value = excess;
    return excess;
  };
  /**
   * Process Payment
   */
  async function processPayment(data: ProcessPaymentRequest) {
    const response = await _quoteSessionService.processPayment(
      currentPolicy.value?.policyNumber ?? '',
      quoteSessionId.value ?? '',
      data
    );
    errors.value = response.errors;
    paymentReceiptNumber.value = response.data?.receiptNumber;
    if (response.errors && response.errors.length > 0) {
      throw response.errors[0].message ?? JP_GENERIC_CODE;
    }
    return response;
  }

  const mapUpgradeMotorCarDetails = async (formData?: Record<string, any>) => {
    model.value!.hasAftermarketAlarmOrEngineImmobiliser =
      toBool(formData?.hasAfterMarketAlarm) || toBool(formData?.hasEngineImmobiliser);
    model.value!.vehicleAccessories = [];
    if (toBool(formData?.hasAfterMarketAlarm)) {
      model.value!.vehicleAccessories?.push('A-Alarm');
    }
    if (toBool(formData?.hasEngineImmobiliser)) {
      model.value!.vehicleAccessories?.push('A-Immobilizer');
    }
    model.value!.unrepairedCollisionOrHailDamage = toBool(formData?.hasDamage);
    if (toBool(formData?.hasAccessories)) {
      model.value!.vehicleAccessories = model.value!.vehicleAccessories?.concat(formData?.accessories);
    }
    model.value!.registeredInBusinessNameOrUsedForBusinessPurposes = (formData as any)?.registeredInBusinessNameOrUsedForBusinessPurposes;
    const excessValues = await getMotorExcess(
      QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive],
      model.value!.changeEffectiveDate.toString()
    );
    model.value!.standardExcessCode = excessValues.find((item: INameCodeWithDefault) => item.isDefault)?.code;
    model.value!.includeCarHire = false;
    model.value!.includeNcdp = false;
    model.value!.includeWindscreenReplacement = false;
  };
  const mapIncludedBenefits = (): string[] => {
    const optionalBenefitOptions = [];
    if (businessRuleResponse.value?.viewModel?.eligibleForFreeCarHire) {
      optionalBenefitOptions.push(MotorOptionalBenefits.HireCar);
    }
    if (businessRuleResponse.value?.viewModel?.eligibleForFreeNcdp) {
      optionalBenefitOptions.push(MotorOptionalBenefits.NCDProtection);
    }
    if (businessRuleResponse.value?.viewModel?.eligibleForFreeWindscreenReplacement) {
      optionalBenefitOptions.push(MotorOptionalBenefits.Windscreen);
    }
    return optionalBenefitOptions;
  };

  const mapModelChanges = (formData: Record<string, any>, vehicleValueType: string) => {
    model.value!.agreedVehicleValue =
      vehicleValueType == QuoteVehicleValuationMethods.AGREED_VALUE ? formData.vehicleAgreedValue : undefined;

    model.value!.includeCarHire = (formData.optionalBenefitOptions as string[])?.includes('hirecar');
    model.value!.includeWindscreenReplacement = (formData.optionalBenefitOptions as string[])?.includes('windscreen');
    model.value!.includeNcdp = (formData.optionalBenefitOptions as string[])?.includes('noClaimDiscountProtection');
    const excess = excessData.value?.find(
      (item: INameCodeWithDefault) => item.code.toString() == formData.excessAmount?.toString()
    );
    if (excess) {
      model.value!.standardExcessCode = excess.code;
      model.value!.standardExcess = excess.name;
    }
  };

  const filterItems = (itemsWrapper: any, viewModel?: UpgradeCoverageViewModel): any => {
    const { items } = itemsWrapper;

    const filteredItems = items.filter((item: any) => {
      if (item.name === MotorOptionalBenefitsSitecoreItem.NCDProtection && !viewModel?.isAllowedToSelectNcdp) {
        return false;
      }
      if (
        item.name === MotorOptionalBenefitsSitecoreItem.Windscreen &&
        !viewModel?.isAllowedToSelectWindscreenReplacement
      ) {
        return false;
      }
      if (item.name === MotorOptionalBenefitsSitecoreItem.HireCar && !viewModel?.isAllowedToSelectCarHire) {
        return false;
      }
      return true;
    });

    return {
      ...itemsWrapper,
      items: filteredItems,
    };
  };
  /**
   * Generate a review summary model for review and confirmation steps
   */
  async function getReviewSummaryModel(summaryFields: any): Promise<IReviewSummary[]> {
    const summaryFieldsResult: IReviewSummary[] = [];

    // The first IReviewSummary item is the changeEffectiveDate section
    const changeEffectiveDate = format(model.value?.changeEffectiveDate ?? '', STANDARD_DATE_FORMAT);
    const insuranceType = await refStoreCommon.getInsuranceProductName(
      QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]
    );
    const insuredType = model.value?.agreedVehicleValue ? VehicleValueType.agreedValue : VehicleValueType.marketValue;
    const policyDetailsObj = {
      insuranceType,
      changeEffectiveDate,
      sumInsured: `${insuredType}${insuredType == VehicleValueType.agreedValue ? ` - ${toCurrency(model.value?.agreedVehicleValue ?? 0, false)}` : ''}`,
      basicExcess: toCurrency(model.value?.standardExcess ?? ''),
      additionalCarHire: '',
      freeWindScreen: '',
      noClaimDiscountProtection: '',
    };
    policyDetailsObj.additionalCarHire =
      businessRuleResponse.value?.viewModel.eligibleForFreeCarHire || model.value?.includeCarHire ? 'Included' : 'None';

    policyDetailsObj.freeWindScreen =
      businessRuleResponse.value?.viewModel.eligibleForFreeWindscreenReplacement ||
      model.value?.includeWindscreenReplacement
        ? 'Included'
        : 'None';

    policyDetailsObj.noClaimDiscountProtection =
      businessRuleResponse.value?.viewModel.eligibleForFreeNcdp || model.value?.includeNcdp ? 'Included' : 'None';

    summaryFields[0] = filterItems(summaryFields[0], businessRuleResponse.value?.viewModel);

    summaryFieldsResult.push(replaceBookmarks(summaryFields[0], policyDetailsObj));

    // The second IReviewSummary item from sitecore is the vehicle summary section. We want to duplicate it for each coverage on the policy.
    const accessories = [] as string[];
    if (model.value?.vehicleAccessories?.length) {
      const accessoriesAndMod = await refMotorStore.getAccessoriesAndModification();

      model.value?.vehicleAccessories.forEach((value: string) => {
        accessories.push(accessoriesAndMod.filter((x) => x.value == value)[0].name);
      });
    }
    const summary = {
      makeAndModel: `${currentCoverage.value?.vehicle?.make} ${currentCoverage.value?.vehicle?.model} ${currentCoverage.value?.vehicle?.registrationNumber}`,
      yearBody: `${currentCoverage.value?.vehicle?.year} ${currentCoverage.value?.vehicle?.series} ${currentCoverage.value?.vehicle?.doors} door ${currentCoverage.value?.vehicle?.transmission}`,
      garagingType: currentCoverage.value?.garagingType,
      suburbAndPostCode: `${currentCoverage.value?.garagingLocation}`,
      finance: currentCoverage.value?.typeOfFinanceCode,
      use: currentCoverage.value?.isVehicleUsePrivate ? 'Private' : 'Business',
      accessoriesAndModifications: accessories?.length ? accessories.join(', ') : 'None',
    };
    let summaryItem = {
      formContentId: summaryFields[1].formContentId,
      summaryTitle: summaryFields[1].summaryTitle,
      icon: summaryFields[1].icon,
      items: summaryFields[1].items,
    };
    summaryItem = replaceBookmarks(summaryItem, summary);
    summaryFieldsResult.push(summaryItem);

    return summaryFieldsResult.map((field: any) => ({
      ...field,
      items: field.items.map((item: ISitecoreReviewSummaryItem) => {
        return {
          ...item,
          ...item.fields,
          editGoToStep: item.fields.editStep?.value,
          showEdit: item.fields.editStep?.value ?? false,
        };
      }),
    }));
  }
  const deleteQuoteSession = async () => {
    try {
      await _quoteSessionService.deleteQuoteSession(quoteSessionId.value ?? '');
    } catch (e) {
      //ignore if delete quote session doesn't work
      console.error('Error delete quote session', e);
    }
  };
  return {
    getPolicy,
    applyBusinessRules,
    processPayment,
    quoteSessionId,
    currentPolicy,
    quotePremiumDetails,
    pushAnalytics,
    jeopardies,
    model,
    paymentReceiptNumber,
    getQuoteSession,
    currentCoverage,
    businessRuleResponse,
    excessData,
    errors,
    mapUpgradeMotorCarDetails,
    riskEffectiveDateBefore10Dec2024,
    getMotorExcess,
    mapIncludedBenefits,
    mapModelChanges,
    getReviewSummaryModel,
    deleteQuoteSession,
  };
});
